import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

// A dialog is a CARD WITH A FIXED CHROME, not a padded box.
//
// `DialogContent` is a flex column that never exceeds the viewport; the header
// and the footer are the chrome and stay put, and `DialogBody` is the only part
// that scrolls. Content therefore goes in `DialogBody` — the content element
// itself carries no padding and no gap, so a dialog cannot double-pad.
//
// Four rules this file exists to keep:
//
// 1. THE CHROME IS THE CARD, THE BODY IS THE PAGE. Header and footer are white
//    (`--color-card`); the body is `bg-muted/50`, which lands on the app's own
//    page ground (#f8fafc). That is the relationship every operator page already
//    has — white cards on a light slate ground — so a white card, table or empty
//    state placed in a dialog body reads exactly as it does on the page behind
//    it. Reported 2026-09-07: header, body and footer were one flat colour.
//
// 2. THE HAIRLINE IS DRAWN FROM THE BODY, NOT FROM THE CHROME. `DialogBody`
//    carries `border-y`, so the two lines exist exactly when there is something
//    to separate. A confirm dialog that is header + footer alone gets no line
//    instead of two adjacent ones, and `last-of-type:border-b-0` drops the line
//    at the bottom edge when a dialog ends on its body.
//
// 3. THE SCROLL CONTAINER MUST NOT CLIP A FOCUS RING. Overflow clips at the
//    PADDING box, so the body's own `px-6 py-5` is the ring's clearance: an
//    `Input`'s `ring-2 ring-offset-2` needs 4px and has 20px+ on every side.
//    `scroll-py-3` then keeps a keyboard-focused field off the scrollport edge,
//    where scroll-into-view would otherwise park it flush and shave the ring.
//    Never reduce the body's padding below `p-2` for that reason.
//
// 4. THE MOTION IS THE STYLESHEET'S, AND IT IS EXPLICIT. `dialog-motion` and
//    `overlay-motion` are defined in the app stylesheet under § THE MOTION
//    SYSTEM — entrypoints/operator.css in the RMS, entrypoints/application.css
//    in the PMS, and the block is the same in both, which is why this file can
//    stay a byte-for-byte copy. A fade and a 150ms scale, keyed off the
//    `data-state` Radix stamps, with a `[data-state='closed']` rule so the
//    dialog LEAVES instead of vanishing. What stood here before was shadcn's
//    `data-[state=open]:animate-in zoom-in-95 fade-in-0` and eight siblings,
//    which are `tailwindcss-animate` utilities: that package is not a dependency
//    of this app, Tailwind emitted nothing for any of them, and the dialog had
//    been popping in and out since the day it was added. A class list is not
//    evidence that an animation runs — `getComputedStyle(el).animationName` is.

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('overlay-motion fixed inset-0 z-50 bg-black/80', className)}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'dialog-motion fixed left-[50%] top-[50%] z-50 flex w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-xl border bg-card p-0 shadow-lg',
        'max-h-[calc(100dvh-4rem)]',
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground opacity-70 ring-offset-background transition-colors hover:bg-muted hover:text-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

// Fixed chrome. `pr-12` keeps a long title clear of the close button.
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex shrink-0 flex-col space-y-1.5 bg-card px-6 pb-4 pr-12 pt-6 text-left', className)}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

// The only scrolling region. See rules 1 and 3 at the top of this file.
const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'min-h-0 flex-1 overflow-y-auto scroll-py-3 border-y border-border/40 bg-muted/50 px-6 py-5 last-of-type:border-b-0',
      className
    )}
    {...props}
  />
)
DialogBody.displayName = 'DialogBody'

// Fixed chrome. `gap-2` rather than `sm:space-x-2` so the stacked mobile layout
// is spaced too.
const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex shrink-0 flex-col-reverse gap-2 bg-card px-6 py-4 sm:flex-row sm:justify-end',
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
